import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

// The legacy key was force-written to `true` by any narrow-viewport visit,
// so its stored values don't reflect a real user choice. A fresh key starts
// everyone from the auto-open default; only explicit wide-screen collapses
// persist under it.
const LEGACY_COLLAPSED_KEY = "assistantSidebarCollapsed";
/** Exported so the loading skeleton can honor a persisted collapse. */
export const SESSION_SIDEBAR_COLLAPSED_KEY = "codesmithSidebarCollapsed";
type SetCollapsed = (value: boolean | ((prev: boolean) => boolean)) => void;

/**
 * Collapsed state for the session sidebar that only auto-opens when the
 * container has room for it (see `usePanelSpaceBudget`). While space exists
 * the stored preference is the source of truth; without space the sidebar
 * starts collapsed regardless, and a manual open is tracked separately so a
 * cramped window never rewrites the wide-screen preference.
 */
export function useSidebarCollapsedState(hasSpaceForSidebar) {
  const $ = _c(10);
  const [storedCollapsed, setStoredCollapsed] = useLocalStorage(SESSION_SIDEBAR_COLLAPSED_KEY, false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  const [openedWhileNarrow, setOpenedWhileNarrow] = useState(false);
  let t1;
  let t2;
  if ($[1] !== hasSpaceForSidebar) {
    t1 = () => {
      if (!hasSpaceForSidebar) {
        setOpenedWhileNarrow(false);
      }
    };
    t2 = [hasSpaceForSidebar];
    $[1] = hasSpaceForSidebar;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const isSidebarCollapsed = hasSpaceForSidebar ? storedCollapsed : !openedWhileNarrow;
  let t3;
  if ($[4] !== hasSpaceForSidebar || $[5] !== setStoredCollapsed) {
    t3 = value => {
      if (hasSpaceForSidebar) {
        setStoredCollapsed(value);
        return;
      }
      setOpenedWhileNarrow(prevOpened => {
        const nextCollapsed = typeof value === "function" ? value(!prevOpened) : value;
        return !nextCollapsed;
      });
    };
    $[4] = hasSpaceForSidebar;
    $[5] = setStoredCollapsed;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const setIsSidebarCollapsed = t3;
  let t4;
  if ($[7] !== isSidebarCollapsed || $[8] !== setIsSidebarCollapsed) {
    t4 = [isSidebarCollapsed, setIsSidebarCollapsed];
    $[7] = isSidebarCollapsed;
    $[8] = setIsSidebarCollapsed;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp() {
  window.localStorage.removeItem(LEGACY_COLLAPSED_KEY);
}